/**
 * Zeitstrahl — Read-only vertikaler Fristen-Zeitstrahl (Baustein 1 "Fristen & Nachhaken")
 *
 * Zeigt die kommenden offenen Aufgaben (nächste ~30 Tage inkl. überfällige),
 * gruppiert nach Fälligkeitsdatum. Überfällige Aufgaben stehen ganz oben in einer
 * eigenen Gruppe "Überfällig". Pro Eintrag: Titel, Kategorie und (falls vorhanden)
 * ein Link zur Akte. Prio-Farbkodierung: dringend=rot, hoch=orange, mittel=gelb,
 * niedrig=grau — im dezenten Stil der App (bg-*-50 / border-*-200).
 *
 * Rein lesend — keine Mutationen.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { CalendarClock, AlertTriangle, ExternalLink, Loader2, Circle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// Prio-Farben (Zeitstrahl-eigen, im dezenten App-Stil analog AufgabenPage)
const PRIORITAET_CONFIG: Record<string, { label: string; dot: string; badge: string }> = {
  dringend: { label: 'Dringend', dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  hoch: { label: 'Hoch', dot: 'bg-orange-500', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  mittel: { label: 'Mittel', dot: 'bg-yellow-500', badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  niedrig: { label: 'Niedrig', dot: 'bg-gray-400', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
};

interface ZeitstrahlAufgabe {
  id: string;
  titel: string | null;
  prioritaet: string | null;
  kategorie: string | null;
  faellig_am: string | null;
  akte_id: string | null;
  akten: { business_unit: string | null; akten_nr: string | null } | null;
}

interface Gruppe {
  key: string;
  label: string;
  ueberfaellig: boolean;
  aufgaben: ZeitstrahlAufgabe[];
}

function heuteDatumString(): string {
  return new Date().toISOString().split('T')[0];
}

function inTagen(tage: number): string {
  const d = new Date();
  d.setDate(d.getDate() + tage);
  return d.toISOString().split('T')[0];
}

function formatGruppenLabel(datum: string): string {
  const heute = heuteDatumString();
  const morgen = inTagen(1);
  if (datum === heute) return 'Heute';
  if (datum === morgen) return 'Morgen';
  return new Date(datum + 'T00:00:00').toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function Zeitstrahl() {
  const [aufgaben, setAufgaben] = useState<ZeitstrahlAufgabe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    async function laden() {
      setLoading(true);
      setError(null);
      const bis = inTagen(30);
      const { data, error: err } = await supabase
        .from('aufgaben')
        .select(
          'id, titel, prioritaet, kategorie, faellig_am, akte_id, akten:akte_id ( business_unit, akten_nr )',
        )
        .eq('status', 'offen')
        .not('faellig_am', 'is', null)
        .lte('faellig_am', bis)
        .order('faellig_am', { ascending: true });

      if (!aktiv) return;
      if (err) {
        setError(err.message);
        setAufgaben([]);
      } else {
        setAufgaben((data ?? []) as unknown as ZeitstrahlAufgabe[]);
      }
      setLoading(false);
    }
    laden();
    return () => {
      aktiv = false;
    };
  }, []);

  const gruppen = useMemo<Gruppe[]>(() => {
    const heute = heuteDatumString();
    const ueberfaellig: ZeitstrahlAufgabe[] = [];
    const nachDatum = new Map<string, ZeitstrahlAufgabe[]>();

    for (const a of aufgaben) {
      if (!a.faellig_am) continue;
      if (a.faellig_am < heute) {
        ueberfaellig.push(a);
      } else {
        const liste = nachDatum.get(a.faellig_am) ?? [];
        liste.push(a);
        nachDatum.set(a.faellig_am, liste);
      }
    }

    const result: Gruppe[] = [];
    if (ueberfaellig.length > 0) {
      result.push({ key: 'ueberfaellig', label: 'Überfällig', ueberfaellig: true, aufgaben: ueberfaellig });
    }
    for (const datum of Array.from(nachDatum.keys()).sort()) {
      result.push({
        key: datum,
        label: formatGruppenLabel(datum),
        ueberfaellig: false,
        aufgaben: nachDatum.get(datum) ?? [],
      });
    }
    return result;
  }, [aufgaben]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Zeitstrahl wird geladen …
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 text-sm text-red-700">
        Zeitstrahl konnte nicht geladen werden: {error}
      </div>
    );
  }

  if (gruppen.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-gray-500">
        Keine anstehenden Fristen in den nächsten 30 Tagen.
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock className="w-5 h-5 text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-900">Zeitstrahl — anstehende Fristen</h2>
      </div>

      <div className="relative pl-4">
        {/* vertikale Linie */}
        <div className="absolute left-1.5 top-1 bottom-1 w-px bg-gray-200" aria-hidden="true" />

        {gruppen.map((gruppe) => (
          <div key={gruppe.key} className="mb-5 last:mb-0">
            <div className="flex items-center gap-2 mb-2">
              {gruppe.ueberfaellig ? (
                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" aria-hidden="true" />
              )}
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${
                  gruppe.ueberfaellig ? 'text-red-700' : 'text-gray-500'
                }`}
              >
                {gruppe.label}
              </span>
              <span className="text-xs text-gray-400">({gruppe.aufgaben.length})</span>
            </div>

            <ul className="space-y-2">
              {gruppe.aufgaben.map((a) => {
                const prio = PRIORITAET_CONFIG[a.prioritaet ?? 'mittel'] || PRIORITAET_CONFIG.mittel;
                const betrieb = a.akten?.business_unit;
                const aktenNr = a.akten?.akten_nr;
                const hatAkteLink = Boolean(a.akte_id && betrieb && aktenNr);
                return (
                  <li
                    key={a.id}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${prio.badge}`}
                  >
                    <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${prio.dot}`} aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {a.titel || 'Ohne Titel'}
                        </span>
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border border-current/20 bg-white/40">
                          {prio.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                        {a.kategorie ? (
                          <span className="inline-flex items-center gap-1">
                            <Circle className="w-2.5 h-2.5" />
                            {a.kategorie}
                          </span>
                        ) : (
                          <span className="text-gray-400">Ohne Kategorie</span>
                        )}
                        {hatAkteLink && (
                          <Link
                            href={`/akten/${betrieb}/${aktenNr}`}
                            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Akte {aktenNr}
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
